import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Pill-shaped brand CTA with a hover arrow micro-interaction.
 * variant: "primary" | "outline" | "ghost"
 */
export const CTA = React.forwardRef(
  ({ children, to, href, variant = "primary", className = "", withArrow = true, testId, ...props }, ref) => {
    const base =
      "group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold tracking-tight transition-all duration-300 active:scale-[0.98] focus-visible:outline-none disabled:opacity-50";
    const variants = {
      primary: "bg-primary text-primary-foreground shadow-soft hover:shadow-lift hover:-translate-y-0.5 hover:brightness-110",
      gradient: "brand-gradient-bg text-white shadow-soft hover:shadow-lift hover:-translate-y-0.5",
      outline: "border border-border bg-transparent text-foreground hover:border-primary/50 hover:bg-primary/5 hover:-translate-y-0.5",
      ghost: "text-foreground hover:text-primary",
    };
    const content = (
      <>
        <span>{children}</span>
        {withArrow && (
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        )}
      </>
    );
    const cls = cn(base, variants[variant], className);

    if (to) {
      return (
        <Link ref={ref} to={to} className={cls} data-testid={testId} {...props}>
          {content}
        </Link>
      );
    }
    if (href) {
      return (
        <a ref={ref} href={href} className={cls} data-testid={testId} {...props}>
          {content}
        </a>
      );
    }
    return (
      <button ref={ref} className={cls} data-testid={testId} {...props}>
        {content}
      </button>
    );
  }
);
CTA.displayName = "CTA";

export default CTA;
